"use client"

import { useState, useCallback, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Database, RefreshCw, Trash2, Download, Upload, AlertTriangle, Settings, CheckCircle } from "lucide-react"

// Types untuk type safety
interface DataStats {
  totalRecords: number
  realRecords: number
  fakeRecords: number
  storageUsed: string
  lastCleanup: Date
  lastBackup: Date
}

interface DataCategory {
  name: string
  real: number
  fake: number
  size: string
  selected?: boolean
}

// Fungsi untuk menghitung ukuran berdasarkan jumlah record
const calculateSize = (records: number): string => {
  const sizeInKB = records * 2;
  const sizeInMB = sizeInKB / 1024;
  const sizeInGB = sizeInMB / 1024;
  
  if (sizeInGB >= 1) {
    return `${sizeInGB.toFixed(1)} GB`;
  } else if (sizeInMB >= 1) {
    return `${sizeInMB.toFixed(1)} MB`;
  } else {
    return `${sizeInKB.toFixed(1)} KB`;
  }
};

// Initial data yang akan di-persist
const initialTotalRecords = 1250000;
const initialRealRecords = 890000;
const initialFakeRecords = 360000;

const initialCategories = [
  { name: "Traffic Logs", real: 450000, fake: 180000, size: "", selected: false },
  { name: "Bot Detection", real: 230000, fake: 95000, size: "", selected: false },
  { name: "Security Events", real: 120000, fake: 45000, size: "", selected: false },
  { name: "User Sessions", real: 67000, fake: 28000, size: "", selected: false },
  { name: "Analytics Data", real: 23000, fake: 12000, size: "", selected: false },
];

export default function DataManagementContent() {
  // Persistent state menggunakan useRef pattern untuk simulasi database
  const [persistentData, setPersistentData] = useState(() => {
    return {
      stats: {
        totalRecords: initialTotalRecords,
        realRecords: initialRealRecords,
        fakeRecords: initialFakeRecords,
        storageUsed: calculateSize(initialTotalRecords),
        lastCleanup: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        lastBackup: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      },
      categories: initialCategories.map(cat => ({
        ...cat,
        size: calculateSize(cat.real + cat.fake)
      }))
    };
  });

  // Current view state (selalu sync dengan persistent data)
  const [dataStats, setDataStats] = useState<DataStats>(persistentData.stats);
  const [dataCategories, setDataCategories] = useState<DataCategory[]>(persistentData.categories);
  
  const [clearType, setClearType] = useState("fake_only")
  const [confirmDialog, setConfirmDialog] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [exportFormat, setExportFormat] = useState("json")
  const [retentionDays, setRetentionDays] = useState("30")
  const [autoCleanup, setAutoCleanup] = useState(true)
  const [compressOldData, setCompressOldData] = useState(true)
  const [dailyBackups, setDailyBackups] = useState(false)
  const [includeMetadata, setIncludeMetadata] = useState(true)
  const [compressOutput, setCompressOutput] = useState(true)
  const [includeFakeData, setIncludeFakeData] = useState(false)
  const [mergeData, setMergeData] = useState(true)
  const [validateData, setValidateData] = useState(true)
  const [createBackup, setCreateBackup] = useState(true)
  const [successMessage, setSuccessMessage] = useState("")

  // Sync view dengan persistent data setiap kali persistent data berubah
  useEffect(() => {
    setDataStats(persistentData.stats);
    setDataCategories(persistentData.categories);
  }, [persistentData]);

  // Safe number formatting function
  const formatNumber = useCallback((num: number | undefined | null): string => {
    if (typeof num !== "number" || isNaN(num)) return "0"
    return num.toLocaleString()
  }, [])

  // Safe date formatting function
  const formatDate = useCallback((date: Date | string | undefined | null): string => {
    try {
      if (!date) return "Never"
      const dateObj = date instanceof Date ? date : new Date(date)
      if (isNaN(dateObj.getTime())) return "Invalid Date"
      return dateObj.toLocaleDateString()
    } catch {
      return "Invalid Date"
    }
  }, [])

  // Update persistent data dan view
  const updatePersistentData = useCallback((newStats: DataStats, newCategories: DataCategory[]) => {
    const updatedData = {
      stats: newStats,
      categories: newCategories
    };
    setPersistentData(updatedData);
    setDataStats(newStats);
    setDataCategories(newCategories);
  }, []);

  // Show success message
  const showSuccess = useCallback((message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(""), 3000);
  }, []);

  // Fungsi untuk menghitung ulang ukuran kategori
  const recalculateSizes = useCallback((categories: DataCategory[]): DataCategory[] => {
    return categories.map(cat => {
      const totalRecords = cat.real + cat.fake;
      return {
        ...cat,
        size: calculateSize(totalRecords)
      };
    });
  }, []);

  // Toggle category selection untuk selective cleanup
  const toggleCategorySelection = useCallback((index: number) => {
    const newCategories = [...dataCategories];
    newCategories[index].selected = !newCategories[index].selected;
    setDataCategories(newCategories);
  }, [dataCategories]);

  // Safe cleanup handler dengan persistent data
  const handleCleanup = useCallback(async () => {
    if (isProcessing) return

    setIsProcessing(true)
    setConfirmDialog(false)

    try {
      // Simulate cleanup process
      await new Promise((resolve) => setTimeout(resolve, 2000))

      let newStats = { ...persistentData.stats };
      let newCategories = [...persistentData.categories];

      switch (clearType) {
        case "fake_only":
          newStats.fakeRecords = 0
          newStats.totalRecords = newStats.realRecords
          newCategories = newCategories.map(cat => ({ ...cat, fake: 0 }));
          showSuccess(`Successfully removed ${formatNumber(persistentData.stats.fakeRecords)} fake records!`);
          break
          
        case "old_data":
          // Simulate removing 30% of old data
          const oldDataReduction = Math.floor(newStats.totalRecords * 0.3)
          newStats.totalRecords = newStats.totalRecords - oldDataReduction
          newStats.realRecords = Math.max(0, newStats.realRecords - Math.floor(oldDataReduction * 0.7))
          newStats.fakeRecords = Math.max(0, newStats.fakeRecords - Math.floor(oldDataReduction * 0.3))
          
          newCategories = newCategories.map(cat => ({
            ...cat,
            real: Math.max(0, cat.real - Math.floor(cat.real * 0.3)),
            fake: Math.max(0, cat.fake - Math.floor(cat.fake * 0.3))
          }));
          showSuccess(`Successfully removed ${formatNumber(oldDataReduction)} old records!`);
          break
          
        case "selective":
          // Hapus data dari kategori yang dipilih
          let removedRecords = 0;
          newCategories = newCategories.map(cat => {
            if (cat.selected) {
              removedRecords += cat.fake;
              newStats.fakeRecords -= cat.fake;
              newStats.totalRecords -= cat.fake;
              return { ...cat, fake: 0, selected: false };
            }
            return { ...cat, selected: false };
          });
          showSuccess(`Successfully removed ${formatNumber(removedRecords)} records from selected categories!`);
          break
          
        case "all":
          const totalRemoved = newStats.totalRecords;
          newStats.totalRecords = 0
          newStats.realRecords = 0
          newStats.fakeRecords = 0
          newCategories = newCategories.map(cat => ({ ...cat, real: 0, fake: 0 }));
          showSuccess(`Successfully removed all ${formatNumber(totalRemoved)} records!`);
          break
      }

      // Hitung ulang storageUsed dan update timestamps
      newStats.storageUsed = calculateSize(newStats.totalRecords);
      newStats.lastCleanup = new Date();
      newCategories = recalculateSizes(newCategories);

      // Update persistent data
      updatePersistentData(newStats, newCategories);

    } catch (error) {
      console.error("Cleanup failed:", error)
      showSuccess("Cleanup failed. Please try again.");
    } finally {
      setIsProcessing(false)
    }
  }, [clearType, isProcessing, persistentData, formatNumber, recalculateSizes, updatePersistentData, showSuccess])

  // Working export handler
  const exportData = useCallback(async (format: string) => {
    if (isProcessing) return

    setIsProcessing(true)

    try {
      // Simulate export process
      await new Promise((resolve) => setTimeout(resolve, 1500))

      // Create export data
      const exportPayload = {
        stats: includeMetadata ? persistentData.stats : { 
          totalRecords: persistentData.stats.totalRecords,
          realRecords: persistentData.stats.realRecords,
          fakeRecords: includeFakeData ? persistentData.stats.fakeRecords : 0
        },
        categories: persistentData.categories.map(cat => ({
          name: cat.name,
          real: cat.real,
          fake: includeFakeData ? cat.fake : 0,
          size: calculateSize(cat.real + (includeFakeData ? cat.fake : 0))
        })),
        exportDate: new Date().toISOString(),
        format,
        settings: includeMetadata ? {
          retentionDays,
          autoCleanup,
          compressOldData,
          dailyBackups
        } : undefined
      }

      let fileContent = "";
      let mimeType = "application/json";
      
      switch (format) {
        case "json":
          fileContent = JSON.stringify(exportPayload, null, 2);
          break;
        case "csv":
          // Convert to CSV format
          const csvHeaders = "Category,Real Records,Fake Records,Size\n";
          const csvData = exportPayload.categories.map(cat => 
            `"${cat.name}",${cat.real},${cat.fake},"${cat.size}"`
          ).join("\n");
          fileContent = csvHeaders + csvData;
          mimeType = "text/csv";
          break;
        case "xml":
          fileContent = `<?xml version="1.0" encoding="UTF-8"?>
<data>
  <stats>
    <totalRecords>${exportPayload.stats.totalRecords}</totalRecords>
    <realRecords>${exportPayload.stats.realRecords}</realRecords>
    <fakeRecords>${exportPayload.stats.fakeRecords}</fakeRecords>
  </stats>
  <categories>
    ${exportPayload.categories.map(cat => 
      `<category><name>${cat.name}</name><real>${cat.real}</real><fake>${cat.fake}</fake><size>${cat.size}</size></category>`
    ).join('\n    ')}
  </categories>
</data>`;
          mimeType = "application/xml";
          break;
        case "sql":
          fileContent = `-- Data Export SQL Dump
INSERT INTO data_stats (total_records, real_records, fake_records) VALUES (${exportPayload.stats.totalRecords}, ${exportPayload.stats.realRecords}, ${exportPayload.stats.fakeRecords});
${exportPayload.categories.map(cat => 
  `INSERT INTO data_categories (name, real_count, fake_count, size) VALUES ('${cat.name}', ${cat.real}, ${cat.fake}, '${cat.size}');`
).join('\n')}`;
          mimeType = "application/sql";
          break;
      }

      const blob = new Blob([fileContent], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `data-export-${Date.now()}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      showSuccess(`Data exported successfully as ${format.toUpperCase()}!`);
    } catch (error) {
      console.error("Export failed:", error)
      showSuccess("Export failed. Please try again.");
    } finally {
      setIsProcessing(false)
    }
  }, [persistentData, isProcessing, includeMetadata, includeFakeData, retentionDays, autoCleanup, compressOldData, dailyBackups, showSuccess])

  // Working refresh handler
  const refreshData = useCallback(async () => {
    if (isProcessing) return

    setIsProcessing(true)

    try {
      // Simulate data refresh
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Mock new data (simulate real-time updates)
      const newRealIncrease = Math.floor(Math.random() * 1000) + 500;
      const newFakeIncrease = Math.floor(Math.random() * 300) + 100;
      
      const newStats = {
        ...persistentData.stats,
        realRecords: persistentData.stats.realRecords + newRealIncrease,
        fakeRecords: persistentData.stats.fakeRecords + newFakeIncrease,
        totalRecords: persistentData.stats.totalRecords + newRealIncrease + newFakeIncrease,
      };
      newStats.storageUsed = calculateSize(newStats.totalRecords);

      // Update some categories randomly
      const newCategories = persistentData.categories.map(cat => {
        const realIncrease = Math.floor(Math.random() * 200);
        const fakeIncrease = Math.floor(Math.random() * 100);
        return {
          ...cat,
          real: cat.real + realIncrease,
          fake: cat.fake + fakeIncrease,
        };
      });

      const recalculatedCategories = recalculateSizes(newCategories);
      updatePersistentData(newStats, recalculatedCategories);
      showSuccess(`Data refreshed! Added ${formatNumber(newRealIncrease + newFakeIncrease)} new records.`);
    } catch (error) {
      console.error("Refresh failed:", error)
      showSuccess("Refresh failed. Please try again.");
    } finally {
      setIsProcessing(false)
    }
  }, [persistentData, isProcessing, recalculateSizes, updatePersistentData, formatNumber, showSuccess])

  // Mock backup function
  const createBackupNow = useCallback(async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newStats = { ...persistentData.stats, lastBackup: new Date() };
      updatePersistentData(newStats, persistentData.categories);
      showSuccess("Backup created successfully!");
    } catch (error) {
      showSuccess("Backup failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, persistentData, updatePersistentData, showSuccess]);

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center space-x-3">
          <CheckCircle className="h-5 w-5 text-green-400" />
          <span className="text-green-400 text-sm">{successMessage}</span>
        </div>
      )}

      {/* Data Overview */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <Database className="mr-2 h-5 w-5 text-cyan-500" />
              Data Management & Cleanup
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Badge className="bg-slate-700/50 text-slate-300 border-slate-600/50">
                Last cleanup: {formatDate(dataStats.lastCleanup)}
              </Badge>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={refreshData} disabled={isProcessing}>
                <RefreshCw className={`h-4 w-4 ${isProcessing ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="bg-slate-800/50 p-1 mb-4">
              <TabsTrigger
                value="overview"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Database className="h-4 w-4 mr-2" />
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="cleanup"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Cleanup
              </TabsTrigger>
              <TabsTrigger
                value="export"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Download className="h-4 w-4 mr-2" />
                Export/Import
              </TabsTrigger>
              <TabsTrigger
                value="settings"
                className="data-[state=active]:bg-slate-700 data-[state=active]:text-cyan-400"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <div className="text-2xl font-bold text-slate-100">{formatNumber(dataStats.totalRecords)}</div>
                  <div className="text-sm text-slate-400">Total Records</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <div className="text-2xl font-bold text-green-400">{formatNumber(dataStats.realRecords)}</div>
                  <div className="text-sm text-slate-400">Real Data</div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <div className="text-2xl font-bold text-amber-400">{formatNumber(dataStats.fakeRecords)}</div>
                  <div className="text-sm text-slate-400">Simulated Data</div>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-slate-200">Data Categories</h3>
                {dataCategories.map((category, index) => (
                  <div
                    key={`${category.name}-${index}`}
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded border border-slate-700/30"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-sm text-slate-200">{category.name}</div>
                      <div className="flex space-x-2">
                        <Badge variant="outline" className="text-xs text-green-400 border-green-400/30">
                          {formatNumber(category.real)} real
                        </Badge>
                        <Badge variant="outline" className="text-xs text-amber-400 border-amber-400/30">
                          {formatNumber(category.fake)} fake
                        </Badge>
                      </div>
                    </div>
                    <div className="text-xs text-slate-400">{category.size}</div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="cleanup" className="mt-0">
              <div className="space-y-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Data Cleanup</h3>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-300 mb-2 block">Cleanup Type</Label>
                      <Select value={clearType} onValueChange={setClearType}>
                        <SelectTrigger className="bg-slate-700/50 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fake_only">🎭 Remove Fake Data Only</SelectItem>
                          <SelectItem value="old_data">📅 Remove Old Data (30+ days)</SelectItem>
                          <SelectItem value="selective">🎯 Selective Cleanup</SelectItem>
                          <SelectItem value="all">⚠️ Clear All Data</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {clearType === "fake_only" && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-amber-400">Remove Simulated Data</div>
                            <div className="text-xs text-slate-400 mt-1">
                              This will remove {formatNumber(dataStats.fakeRecords)} simulated records, freeing up
                              approximately {calculateSize(dataStats.fakeRecords)} of storage space. Real traffic data will be preserved.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {clearType === "old_data" && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <AlertTriangle className="h-5 w-5 text-blue-400 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-blue-400">Remove Old Data</div>
                            <div className="text-xs text-slate-400 mt-1">
                              This will remove data older than 30 days. Approximately 30% of current data will be
                              removed.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {clearType === "selective" && (
                      <div className="space-y-3">
                        <div className="text-sm text-slate-300">Select data categories to clean:</div>
                        {dataCategories.map((category, index) => (
                          <div
                            key={`selective-${category.name}-${index}`}
                            className="flex items-center justify-between p-3 bg-slate-900/50 rounded border border-slate-700/30"
                          >
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                className="rounded border-slate-600 bg-slate-700"
                                checked={category.selected || false}
                                onChange={() => toggleCategorySelection(index)}
                              />
                              <div>
                                <div className="text-sm text-slate-200">{category.name}</div>
                                <div className="text-xs text-slate-500">{formatNumber(category.fake)} fake records</div>
                              </div>
                            </div>
                            <div className="text-xs text-slate-400">{category.size}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {clearType === "all" && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5" />
                          <div>
                            <div className="text-sm font-medium text-red-400">⚠️ Danger Zone</div>
                            <div className="text-xs text-slate-400 mt-1">
                              This will permanently delete ALL data including real traffic logs, bot detection results,
                              and security events. This action cannot be undone.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-2">
                      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
                        <DialogTrigger asChild>
                          <Button
                            variant={clearType === "all" ? "destructive" : "default"}
                            className={clearType === "all" ? "" : "bg-amber-600 hover:bg-amber-700"}
                            disabled={isProcessing}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {isProcessing ? "Processing..." : "Start Cleanup"}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-700">
                          <DialogHeader>
                            <DialogTitle className="text-slate-100">Confirm Data Cleanup</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="text-sm text-slate-300">
                              Are you sure you want to proceed with this cleanup operation?
                            </div>

                            {clearType === "fake_only" && (
                              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3">
                                <div className="text-sm text-amber-400">
                                  • {formatNumber(dataStats.fakeRecords)} simulated records will be deleted 
                                  • Approximately {calculateSize(dataStats.fakeRecords)} will be freed 
                                  • Real data will be preserved
                                </div>
                              </div>
                            )}

                            {clearType === "selective" && (
                              <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                                <div className="text-sm text-blue-400">
                                  Selected categories will have their fake data removed:
                                  {dataCategories.filter(cat => cat.selected).map(cat => (
                                    <div key={cat.name}>• {cat.name}: {formatNumber(cat.fake)} records</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {clearType === "all" && (
                              <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                                <div className="text-sm text-red-400">
                                  ⚠️ This will permanently delete ALL {formatNumber(dataStats.totalRecords)} records.
                                  This action cannot be undone.
                                </div>
                              </div>
                            )}

                            <div className="flex justify-end space-x-2">
                              <Button variant="outline" onClick={() => setConfirmDialog(false)}>
                                Cancel
                              </Button>
                              <Button
                                variant={clearType === "all" ? "destructive" : "default"}
                                onClick={handleCleanup}
                                disabled={isProcessing}
                              >
                                {isProcessing ? "Processing..." : "Confirm Cleanup"}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="export" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4 flex items-center">
                    <Download className="mr-2 h-5 w-5 text-green-500" />
                    Export Data
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-300 mb-2 block">Export Format</Label>
                      <Select value={exportFormat} onValueChange={setExportFormat}>
                        <SelectTrigger className="bg-slate-700/50 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="json">📄 JSON Format</SelectItem>
                          <SelectItem value="csv">📊 CSV Format</SelectItem>
                          <SelectItem value="xml">🗂️ XML Format</SelectItem>
                          <SelectItem value="sql">🗄️ SQL Dump</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-sm text-slate-300">Export Options</Label>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="include-metadata" className="text-sm text-slate-400">
                          Include Metadata
                        </Label>
                        <Switch
                          id="include-metadata"
                          checked={includeMetadata}
                          onCheckedChange={setIncludeMetadata}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="include-fake" className="text-sm text-slate-400">
                          Include Fake Data
                        </Label>
                        <Switch
                          id="include-fake"
                          checked={includeFakeData}
                          onCheckedChange={setIncludeFakeData}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="compress-output" className="text-sm text-slate-400">
                          Compress Output
                        </Label>
                        <Switch
                          id="compress-output"
                          checked={compressOutput}
                          onCheckedChange={setCompressOutput}
                        />
                      </div>
                    </div>

                    <Button
                      onClick={() => exportData(exportFormat)}
                      disabled={isProcessing}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {isProcessing ? "Exporting..." : "Export Data"}
                    </Button>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Import Data</h3>
                  
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <Label className="text-sm text-slate-300">Import Options</Label>
                      
                      <div className="flex items-center justify-between">
                        <Label htmlFor="merge-data" className="text-sm text-slate-400">
                          Merge with Existing Data
                        </Label>
                        <Switch
                          id="merge-data"
                          checked={mergeData}
                          onCheckedChange={setMergeData}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="validate-data" className="text-sm text-slate-400">
                          Validate Data
                        </Label>
                        <Switch
                          id="validate-data"
                          checked={validateData}
                          onCheckedChange={setValidateData}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="create-backup" className="text-sm text-slate-400">
                          Create Backup Before Import
                        </Label>
                        <Switch
                          id="create-backup"
                          checked={createBackup}
                          onCheckedChange={setCreateBackup}
                        />
                      </div>
                    </div>

                    <div className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center">
                      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <div className="text-sm text-slate-400 mb-2">Drop files here or click to browse</div>
                      <div className="text-xs text-slate-500">Supports JSON, CSV, XML files</div>
                      <Button variant="outline" className="mt-3" disabled={isProcessing}>
                        <Upload className="h-4 w-4 mr-2" />
                        Select Files
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="mt-0">
              <div className="space-y-6">
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Automated Settings</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-slate-300 mb-2 block">Data Retention (Days)</Label>
                      <Select value={retentionDays} onValueChange={setRetentionDays}>
                        <SelectTrigger className="bg-slate-700/50 border-slate-600">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">7 Days</SelectItem>
                          <SelectItem value="14">14 Days</SelectItem>
                          <SelectItem value="30">30 Days</SelectItem>
                          <SelectItem value="60">60 Days</SelectItem>
                          <SelectItem value="90">90 Days</SelectItem>
                          <SelectItem value="365">1 Year</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="auto-cleanup" className="text-sm text-slate-300">
                          Auto Cleanup
                        </Label>
                        <Switch
                          id="auto-cleanup"
                          checked={autoCleanup}
                          onCheckedChange={setAutoCleanup}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="compress-old" className="text-sm text-slate-300">
                          Compress Old Data
                        </Label>
                        <Switch
                          id="compress-old"
                          checked={compressOldData}
                          onCheckedChange={setCompressOldData}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <Label htmlFor="daily-backups" className="text-sm text-slate-300">
                          Daily Backups
                        </Label>
                        <Switch
                          id="daily-backups"
                          checked={dailyBackups}
                          onCheckedChange={setDailyBackups}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <h3 className="text-lg font-semibold text-slate-200 mb-4">Storage Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="text-lg font-semibold text-slate-200">{dataStats.storageUsed}</div>
                      <div className="text-sm text-slate-400">Total Storage Used</div>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="text-lg font-semibold text-slate-200">{formatDate(dataStats.lastBackup)}</div>
                      <div className="text-sm text-slate-400">Last Backup</div>
                    </div>
                  </div>
                  
                  <Button
                    onClick={createBackupNow}
                    disabled={isProcessing}
                    className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
                  >
                    <Database className="h-4 w-4 mr-2" />
                    {isProcessing ? "Creating Backup..." : "Create Backup Now"}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}
